import { Component, effect, inject, OnDestroy, signal, untracked } from '@angular/core';
import { FormControl, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormDatePickerComponent, FormInputComponent, FormSelectComponent, FormTextareaComponent, FormToggleSwitchComponent } from '@shared/forms';
import { MaterialListComponent, MediaImageComponent, UnavailableComponent } from '@shared/components';
import { CourseFacade } from '@app/store/course';
import { FormOptionsFacade } from '@app/store/form-options';
import { AssessmentFacade } from '@app/store/assessment';
import { COURSE_TYPE_OPTIONS, CourseType, DEFAULT_COURSE_TYPE, MediaType } from '@app/store/shared/models';
import { ModuleSelection, ModuleSelectionService, MediaSelectionService, MediaTarget } from '@app/dialogs';
import { ModuleListComponent } from '../module-list/module-list.component';
import { AssessmentComponent } from '../assessment/assessment.component';

@Component({
    selector: 'app-course-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        FormInputComponent,
        FormSelectComponent,
        FormTextareaComponent,
        FormDatePickerComponent,
        FormToggleSwitchComponent,
        UnavailableComponent,
        MediaImageComponent,
        ModuleListComponent,
        MaterialListComponent,
        AssessmentComponent,
    ],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly courseFacade = inject(CourseFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly assessmentFacade = inject(AssessmentFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly moduleSelectionService = inject(ModuleSelectionService);
    private readonly mediaSelectionService = inject(MediaSelectionService);
    private readonly translateService = inject(TranslateService);

    readonly course = this.courseFacade.course;
    readonly loading = this.courseFacade.loading;
    readonly saving = this.courseFacade.saving;
    readonly error = this.courseFacade.error;
    readonly instructors = this.formOptionsFacade.instructors;
    readonly typeOptions = COURSE_TYPE_OPTIONS.map((option) => ({
        ...option,
        label: this.translateService.instant(option.label),
    }));

    readonly selectedModules = signal<ModuleSelection[]>([]);

    readonly minDeadline = (() => {
        const date = new Date();
        date.setDate(date.getDate() + 1);
        return date;
    })();

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        description: this.formBuilder.control(''),
        type: this.formBuilder.control<CourseType>(DEFAULT_COURSE_TYPE, [FormValidators.Required]),
        instructorId: this.formBuilder.control<string | null>(null),
        materialIds: this.formBuilder.control<string[]>([]),
        mediaPlaceholderId: new FormControl<string | null>(null),
        mediaPlaceholderPortraitId: new FormControl<string | null>(null),
        deadline: new FormControl<Date | null>(null),
        orderRestricted: this.formBuilder.control(false),
    });

    constructor() {
        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.courseFacade.getCourse(id);
        }

        if (this.formOptionsFacade.instructorsLoaded() !== true) {
            this.formOptionsFacade.getInstructors();
        }

        if (this.formOptionsFacade.modulesLoaded() !== true) {
            this.formOptionsFacade.getModules();
        }

        effect(() => {
            const course = this.course();
            const moduleOptions = this.formOptionsFacade.modules();
            if (course) {
                this.form.patchValue({
                    name: course.name,
                    description: course.description,
                    type: course.type,
                    instructorId: course.instructorId,
                    materialIds: course.materialIds,
                    mediaPlaceholderId: course.mediaPlaceholderId,
                    mediaPlaceholderPortraitId: course.mediaPlaceholderPortraitId,
                    deadline: course.deadline,
                    orderRestricted: course.orderRestricted,
                });

                if (moduleOptions.length > 0 && untracked(this.selectedModules).length === 0) {
                    const modules = course.moduleIds
                        .map((id) => {
                            const option = moduleOptions.find((o) => o.value === id);
                            return option ? { id: option.value, name: option.label } : null;
                        })
                        .filter((m): m is ModuleSelection => m !== null);

                    this.selectedModules.set(modules);
                }
            }
        });

        effect(() => {
            if (this.courseFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.courseFacade.saving() === false && this.courseFacade.saved() === false) {
                this.form.enable();
            }
        });

        effect(() => {
            const selection = this.moduleSelectionService.selected();
            if (selection) {
                this.selectedModules.update((modules) => {
                    if (modules.some((m) => m.id === selection.id)) {
                        return modules;
                    }
                    return [...modules, selection];
                });
                this.moduleSelectionService.clear();
            }
        });

        effect(() => {
            const selection = this.mediaSelectionService.selected();
            if (!selection) return;

            if (selection.target === 'materialIds') {
                const current = this.form.controls.materialIds.value ?? [];
                const id = selection.media.id;
                if (!current.includes(id)) {
                    this.form.controls.materialIds.setValue([...current, id]);
                }
            } else if (selection.target === 'mediaPlaceholderId') {
                this.form.controls.mediaPlaceholderId.setValue(selection.media.id);
            } else if (selection.target === 'mediaPlaceholderPortraitId') {
                this.form.controls.mediaPlaceholderPortraitId.setValue(selection.media.id);
            } else {
                return;
            }

            this.mediaSelectionService.clear();
        });
    }

    ngOnDestroy(): void {
        this.courseFacade.clear();
        this.formOptionsFacade.clear();
        this.assessmentFacade.clear();
    }

    openModuleDialog(): void {
        this.router.navigate([{ outlets: { dialog: ['select-module'] } }]);
    }

    removeModule(moduleId: string): void {
        this.selectedModules.update((modules) => modules.filter((m) => m.id !== moduleId));
    }

    reorderModules(modules: ModuleSelection[]): void {
        this.selectedModules.set(modules);
    }

    onSelectMedia(target: MediaTarget, mediaType: MediaType | MediaType[]): void {
        const value = Array.isArray(mediaType) ? mediaType.join(',') : mediaType;
        this.router.navigate([{ outlets: { dialog: ['select-media', { mediaTarget: target, mediaType: value }] } }]);
    }

    onSelectMaterial(): void {
        this.onSelectMedia('materialIds', ['pdf', 'image']);
    }

    removeMaterial(id: string): void {
        const current = this.form.controls.materialIds.value ?? [];
        this.form.controls.materialIds.setValue(current.filter((mid) => mid !== id));
    }

    reorderMaterials(ids: string[]): void {
        this.form.controls.materialIds.setValue(ids);
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const course = this.course();
        if (this.form.valid && course) {
            this.form.disable();
            const formValue = this.form.getRawValue();
            this.courseFacade.updateCourse(course.id, {
                name: formValue.name,
                description: formValue.description,
                type: formValue.type,
                objectives: course.objectives,
                instructorId: formValue.instructorId,
                tagIds: course.tagIds,
                moduleIds: this.selectedModules().map((m) => m.id),
                materialIds: formValue.materialIds,
                mediaPlaceholderId: formValue.mediaPlaceholderId,
                mediaPlaceholderPortraitId: formValue.mediaPlaceholderPortraitId,
                originalMediaPlaceholderId: course.mediaPlaceholderId,
                originalMediaPlaceholderPortraitId: course.mediaPlaceholderPortraitId,
                deadline: formValue.deadline,
                orderRestricted: formValue.orderRestricted,
            });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
